<template>
  <div class="multi-file-upload-wrapper">
    <label v-if="label" class="block text-sm font-medium text-text mb-2">
      {{ label }}
      <span v-if="required" class="text-error ml-1">*</span>
      <span v-if="uploadedFiles.length > 0" class="text-text-muted font-normal ml-2">
        ({{ uploadedFiles.length }} {{ uploadedFiles.length === 1 ? 'file' : 'files' }})
      </span>
    </label>

    <!-- Upload Area -->
    <div
      class="border-2 border-dashed rounded-lg p-6 text-center transition-colors"
      :class="[
        isDragging
          ? 'border-primary bg-primary/10'
          : 'border-border bg-surface-secondary'
      ]"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="handleDrop"
    >
      <input
        ref="fileInput"
        type="file"
        :accept="acceptedTypes"
        multiple
        :disabled="disabled"
        class="hidden"
        @change="handleFileSelect"
      />
      
      <div v-if="uploadedFiles.length === 0" class="space-y-2">
        <svg class="mx-auto h-12 w-12 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
        <p class="text-sm text-text-muted">
          <span class="font-medium text-primary cursor-pointer" @click="triggerFileInput">Click to upload</span>
          or drag and drop
        </p>
        <p class="text-xs text-text-muted">{{ acceptedTypesText }}</p>
        <p class="text-xs text-text-muted">{{ sizeLimitHint }}</p>
    </div>

      <div v-else class="space-y-2">
        <!-- Upload Progress Bar (when files are uploading) -->
        <div v-if="isUploading" class="w-full space-y-2">
          <div class="flex items-center space-x-3">
            <svg class="w-6 h-6 animate-spin text-primary" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <div class="flex-1">
              <p class="text-sm font-medium text-text">Uploading files...</p>
              <p class="text-xs text-text-muted">{{ uploadStatusText }}</p>
            </div>
          </div>
          <div class="w-full bg-surface-muted rounded-full h-2.5">
              <div
              class="bg-primary h-2.5 rounded-full transition-all duration-300" 
              :style="{ width: `${uploadProgressPercent}%` }"
              ></div>
          </div>
          <p class="text-xs text-text-muted text-center">
            {{ uploadedCount }} of {{ totalUploadingCount }} files uploaded
            <span v-if="failedCount > 0" class="text-error font-medium"> ({{ failedCount }} failed)</span>
          </p>
        </div>

        <!-- Success State (when files are selected but not uploading) -->
        <div v-else>
          <svg class="mx-auto h-12 w-12 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          <div>
            <p class="text-sm font-medium text-text">{{ uploadedFiles.length }} {{ uploadedFiles.length === 1 ? 'file' : 'files' }} selected</p>
            <p class="text-xs text-text-muted">{{ formatTotalSize() }}</p>
          </div>
          <BaseButton
            variant="ghost"
            size="small"
            @click.stop="onClearClick"
            :disabled="disabled"
          >
            Clear
          </BaseButton>
        </div>
      </div>
    </div>

    <!-- Add More Button -->
    <div v-if="uploadedFiles.length > 0" class="mt-4">
      <BaseButton
        variant="secondary"
        size="small"
        @click="triggerFileInput"
        :disabled="disabled"
      >
        Add More Files
      </BaseButton>
    </div>

    <!-- Uploaded Files List -->
    <div v-if="uploadedFiles.length > 0" class="mt-4">
      <div class="grid grid-cols-2 gap-4">
        <div
          v-for="(file, index) in uploadedFiles"
          :key="file.id || index"
          class="relative group border border-border rounded-lg bg-surface overflow-hidden"
        >
          <!-- Image Preview or File Icon -->
          <div class="aspect-square relative">
            <!-- Image Preview (if image) -->
            <img
              v-if="isImageFile(file) && file.previewUrl && !file.imageError"
              :src="file.previewUrl"
              :alt="file.name"
              class="w-full h-full object-cover"
              @error="(e) => handleImageError(e, file)"
            />
            <!-- File Icon (if document) -->
            <div
              v-else
              class="w-full h-full bg-surface-secondary flex items-center justify-center"
            >
              <svg class="h-12 w-12 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            
            <!-- Overlay with actions -->
            <div class="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <a
                v-if="file.url"
                :href="file.url"
                target="_blank"
                rel="noopener noreferrer"
                class="text-white hover:text-white hover:bg-white/20 p-2 rounded"
                @click.stop
              >
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
              <BaseButton
                v-if="file.error"
                variant="ghost"
                size="small"
                class="text-white hover:text-white hover:bg-white/20"
                @click.stop="retryUpload(file, index)"
              >
                Retry
              </BaseButton>
              <BaseButton
                variant="ghost"
                size="small"
                class="text-white hover:text-white hover:bg-white/20"
                @click.stop="requestRemove(index)"
              >
                Remove
              </BaseButton>
            </div>
            
            <!-- Upload Progress Overlay -->
            <div v-if="file.uploading" class="absolute inset-0 bg-black/60 flex flex-col items-center justify-center p-2">
              <div class="w-full bg-surface-secondary rounded-full h-2 mb-2">
                <div
                  class="bg-primary h-2 rounded-full transition-all"
                  :style="{ width: `${file.progress || 0}%` }"
                ></div>
              </div>
              <p class="text-xs text-white">Uploading... {{ file.progress || 0 }}%</p>
            </div>
            
            <!-- Error Badge -->
            <div v-if="file.error" class="absolute top-2 left-2 bg-error text-white text-xs px-2 py-1 rounded">
              Error
            </div>
            
            <!-- Success Badge -->
            <div v-else-if="file.url && !file.uploading" class="absolute top-2 left-2 bg-success text-white text-xs px-2 py-1 rounded">
              Uploaded
            </div>
          </div>
          
          <!-- File Name (below preview/icon) -->
          <div class="p-2">
            <p class="text-xs font-medium text-text truncate" :title="file.name">{{ file.name }}</p>
            <p class="text-xs text-text-muted">{{ getFileType(file.name) }} • {{ formatFileSize(file.size) }}</p>
          </div>
        </div>
      </div>
    </div>

    <div v-if="errorText" class="mt-1 text-sm text-error">
      {{ errorText }}
    </div>
    <div v-if="helpText && !errorText" class="mt-1 text-sm text-text-muted">
      {{ helpText }}
    </div>

    <BaseConfirmDialog
      v-model="showRemoveConfirm"
      :title="removeConfirmTitle"
      :message="removeConfirmMessage"
      confirm-label="Delete"
      cancel-label="Cancel"
      confirm-variant="danger"
      layout="default"
      @confirm="onRemoveConfirm"
      @cancel="onRemoveCancel"
    />
    <BaseConfirmDialog
      v-model="showClearConfirm"
      :title="clearConfirmTitle"
      :message="clearConfirmMessage"
      :confirm-label="clearConfirmLabel"
      cancel-label="Cancel"
      confirm-variant="danger"
      layout="default"
      @confirm="onClearConfirm"
      @cancel="onClearCancel"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue'
import BaseButton from '@/components/navigation/BaseButton.vue'
import BaseConfirmDialog from '@/components/feedback/BaseConfirmDialog.vue'
import {
  isAllowedPimImageAndAssetFile,
  isAllowedUniversalIntakeFile,
} from '@/constants/pimFileUploadGroups'

interface UploadedFile {
  id: string
  file?: File
  name: string
  size: number
  previewUrl?: string
  url?: string
  uploading?: boolean
  progress?: number
  error?: string
  imageError?: boolean // Flag to track if image failed to load
}

interface Props {
  modelValue?: (string | File)[] // URLs or staged File objects
  label?: string
  required?: boolean
  disabled?: boolean
  helpText?: string
  errorText?: string
  productUuid?: string
  maxFiles?: number
  acceptedTypes?: string
  /** When true, files are staged only; parent uploads on save. Temp files are preserved when modelValue changes. */
  deferUpload?: boolean
  requireRemoveConfirmation?: boolean
  requireClearConfirmation?: boolean
  removeConfirmTitle?: string
  removeConfirmMessage?: string
  clearConfirmTitle?: string
  clearConfirmMessage?: string
  clearConfirmLabel?: string
  /** Max size per file in bytes (default 10MB). */
  maxFileSizeBytes?: number
  /** When true, validates against PIM combined image+asset allowlist (matches product uploads). */
  usePimImageAndAssetValidation?: boolean
  /** When true, validates against Universal Intake allowlist (narrower than PIM). Takes precedence over `usePimImageAndAssetValidation`. */
  useUniversalIntakeValidation?: boolean
  /** When set, staged files must not exceed this total size in bytes (e.g. combined upload cap). */
  maxTotalBytes?: number
}

const props = withDefaults(defineProps<Props>(), {
  required: false,
  disabled: false,
  maxFiles: 20,
  acceptedTypes: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.svg,.indd,.indt',
  maxFileSizeBytes: 10 * 1024 * 1024,
  usePimImageAndAssetValidation: false,
  useUniversalIntakeValidation: false,
  maxTotalBytes: undefined,
  deferUpload: false,
  requireRemoveConfirmation: false,
  requireClearConfirmation: false,
  removeConfirmTitle: 'Confirm deletion',
  removeConfirmMessage: 'Are you sure you want to delete this item?',
  clearConfirmTitle: 'Clear all files?',
  clearConfirmMessage: 'Are you sure you want to remove all files from the list?',
  clearConfirmLabel: 'Clear all'
})

const emit = defineEmits<{
  'update:modelValue': [value: (string | File)[]]
  'files-selected': [files: File[]]
  'file-uploaded': [file: UploadedFile]
  'file-removed': [index: number]
  'clear-all': []
  'validation-error': [message: string]
}>()

const fileInput = ref<HTMLInputElement>()
const isDragging = ref(false)
const uploadedFiles = ref<UploadedFile[]>([])
const showRemoveConfirm = ref(false)
const showClearConfirm = ref(false)
const pendingRemoveIndex = ref<number | null>(null)

watch(showRemoveConfirm, (open) => {
  if (!open) pendingRemoveIndex.value = null
})

// Upload progress tracking
const isUploading = computed(() => {
  return uploadedFiles.value.some(file => file.uploading)
})

const totalUploadingCount = computed(() => {
  return uploadedFiles.value.filter(file => file.file || file.uploading).length
})

const uploadedCount = computed(() => {
  return uploadedFiles.value.filter(file => file.url && !file.uploading && !file.error).length
})

const failedCount = computed(() => {
  return uploadedFiles.value.filter(file => file.error).length
})

const uploadProgressPercent = computed(() => {
  if (totalUploadingCount.value === 0) return 0
  return Math.round((uploadedCount.value / totalUploadingCount.value) * 100)
})

const maxFileSizeDisplay = computed(() => {
  const b = props.maxFileSizeBytes
  if (b >= 1024 * 1024) {
    const mb = Math.round(b / (1024 * 1024))
    return `${mb}MB`
  }
  const kb = Math.max(1, Math.round(b / 1024))
  return `${kb}KB`
})

function formatBytesShort(b: number): string {
  if (b >= 1024 * 1024) {
    const mb = Math.round(b / (1024 * 1024))
    return `${mb} MB`
  }
  const kb = Math.max(1, Math.round(b / 1024))
  return `${kb} KB`
}

const sizeLimitHint = computed(() => {
  const per = `Up to ${maxFileSizeDisplay.value} per file`
  if (props.maxTotalBytes != null) {
    return `${per}; ${formatBytesShort(props.maxTotalBytes)} total for all attachments`
  }
  return `${per} each`
})

const uploadStatusText = computed(() => {
  if (failedCount.value > 0) {
    return `Uploading... (${failedCount.value} failed)`
  }
  if (uploadedCount.value === totalUploadingCount.value && totalUploadingCount.value > 0) {
    return 'Upload completed successfully'
  }
  return `Uploading file ${uploadedCount.value + 1} of ${totalUploadingCount.value}...`
})

const acceptedTypesText = computed(() => {
  if (!props.acceptedTypes) {
    return `Accepted: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, CSV, SVG, INDD, INDT`
  }
  
  // Check for font file types
  const fontExtensions = ['.ttf', '.otf', '.woff', '.woff2', '.eot', '.ttc']
  const hasFontTypes = fontExtensions.some(ext => props.acceptedTypes?.includes(ext))
  
  if (hasFontTypes) {
    return `Accepted: Font files (.ttf, .otf, .woff, .woff2, .eot, .ttc)`
  }
  
  // Check for image types
  if (props.acceptedTypes.includes('image/*')) {
    // Parse all document types from acceptedTypes
    const docTypes = ['PDF', 'DOC', 'DOCX', 'XLS', 'XLSX', 'PPT', 'PPTX', 'TXT', 'CSV', 'SVG', 'INDD', 'INDT']
    const presentTypes = docTypes.filter(type => {
      const lowerType = type.toLowerCase()
      return props.acceptedTypes?.includes(`.${lowerType}`) || props.acceptedTypes?.includes(lowerType)
    })
    
    if (presentTypes.length > 0) {
      return `Accepted: Images, ${presentTypes.join(', ')}`
    }
    return `Accepted: Images, PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, CSV, SVG, INDD, INDT`
  }
  
  // Default document types
  return `Accepted: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, CSV, SVG, INDD, INDT`
})

// Initialize from modelValue (existing URLs or File objects)
watch(() => props.modelValue, (newValue) => {
  // Check if newValue is truly empty
  const isEmpty = !newValue || !Array.isArray(newValue) || newValue.length === 0 || newValue.every(v => !v)
  
  if (isEmpty) {
    // If no URLs, preserve only temporary files (files that have been selected but not yet uploaded)
    const tempFiles = uploadedFiles.value.filter(f => f.file && !f.url)
    uploadedFiles.value = tempFiles
    return
  }
  
  const newItems = newValue.filter(Boolean)
  
  // Build files from modelValue - handle both URLs (strings) and File objects
  const processedFiles = newItems.map((item: string | File, index) => {
    // Check if item is a File object
    if (item instanceof File) {
      const isImage = item.type.startsWith('image/')
      return {
        id: `file-${index}-${Date.now()}-${Math.random()}`,
        file: item,
        name: item.name,
        size: item.size,
        previewUrl: isImage ? URL.createObjectURL(item) : undefined,
        uploading: false
      }
    }
    
    // Handle string URLs (existing uploaded files)
    const urlString = typeof item === 'string' ? item : String(item)
    
    // Check if URL is an image by extension
    const isImage = /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(urlString)
    return {
      id: `existing-${index}-${Date.now()}-${Math.random()}`,
      name: urlString.split('/').pop() || `File ${index + 1}`,
      size: 0,
      url: urlString,
      previewUrl: isImage ? urlString : undefined // Set preview URL for images
    }
  })
  
  // Cleanup old blob URLs before replacing (only for files we're removing)
  if (!props.deferUpload) {
    uploadedFiles.value.forEach(file => {
      if (file.previewUrl && file.previewUrl.startsWith('blob:') && !processedFiles.find(pf => pf.previewUrl === file.previewUrl)) {
        URL.revokeObjectURL(file.previewUrl)
      }
    })
  }
  
  if (props.deferUpload) {
    const existingTempFiles = uploadedFiles.value.filter(f => f.file && !f.url)
    uploadedFiles.value = [...processedFiles, ...existingTempFiles]
  } else {
    uploadedFiles.value = processedFiles
  }
}, { immediate: true, deep: true })

const triggerFileInput = () => {
  fileInput.value?.click()
}

const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files && target.files.length > 0) {
    handleFiles(Array.from(target.files))
  }
}

const handleDrop = (event: DragEvent) => {
  isDragging.value = false
  if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
    handleFiles(Array.from(event.dataTransfer.files))
  }
}

const isValidFileType = (file: File): boolean => {
  // Accept images
  if (file.type.startsWith('image/')) {
    return true
  }
  
  // Check if file matches acceptedTypes prop
  if (props.acceptedTypes) {
    const acceptedTypesList = props.acceptedTypes.split(',').map(t => t.trim().toLowerCase())
    const fileName = file.name.toLowerCase()
    const fileExtension = '.' + fileName.split('.').pop()?.toLowerCase()
    const fileMimeType = file.type.toLowerCase()
    
    // Check by extension
    if (acceptedTypesList.some(type => fileName.endsWith(type) || fileExtension === type)) {
      return true
    }
    
    // Check by MIME type
    if (acceptedTypesList.some(type => {
      // Handle MIME type patterns like "font/ttf", "application/font-woff"
      if (type.includes('/')) {
        return fileMimeType.includes(type) || fileMimeType === type
      }
      return false
    })) {
      return true
    }
  }
  
  // Default valid types (for backward compatibility)
  const validTypes = [
    'image/svg+xml',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain',
    'text/csv',
    'application/csv',
    // Font MIME types
    'font/ttf',
    'font/otf',
    'application/font-woff',
    'application/font-woff2',
    'application/vnd.ms-fontobject',
    'application/x-font-ttf',
    'application/x-font-opentype',
    // Adobe InDesign (some clients report a dedicated MIME; extension check above is primary)
    'application/x-indesign'
  ]
  return validTypes.includes(file.type)
}

const handleFiles = (files: File[]) => {
  // Check max files limit (count existing URLs from modelValue + temporary files)
  const urlCount = Array.isArray(props.modelValue) ? props.modelValue.filter(Boolean).length : 0
  const tempFileCount = uploadedFiles.value.filter(f => f.file && !f.url).length
  const currentCount = urlCount + tempFileCount

  if (currentCount + files.length > props.maxFiles) {
    emit(
      'validation-error',
      `You can attach at most ${props.maxFiles} file${props.maxFiles === 1 ? '' : 's'}.`
    )
    return
  }

  const existingBytes = uploadedFiles.value
    .filter(f => f.file && !f.url)
    .reduce((sum, f) => sum + (f.size || 0), 0)

  const invalidTypeNames: string[] = []
  const invalidSizeNames: string[] = []
  const accepted: File[] = []

  for (const file of files) {
    let typeOk = false
    if (props.useUniversalIntakeValidation) {
      typeOk = isAllowedUniversalIntakeFile(file)
    } else if (props.usePimImageAndAssetValidation) {
      typeOk = isAllowedPimImageAndAssetFile(file)
    } else {
      const isImage = file.type.startsWith('image/')
      typeOk = isImage || isValidFileType(file)
    }
    if (!typeOk) {
      invalidTypeNames.push(file.name)
      continue
    }
    if (file.size > props.maxFileSizeBytes) {
      invalidSizeNames.push(file.name)
      continue
    }
    accepted.push(file)
  }

  const newBytes = accepted.reduce((sum, f) => sum + f.size, 0)
  if (props.maxTotalBytes != null && existingBytes + newBytes > props.maxTotalBytes) {
    emit(
      'validation-error',
      `The combined size of all files must not exceed ${formatBytesShort(props.maxTotalBytes)}.`
    )
    return
  }

  const feedbackParts: string[] = []
  if (invalidTypeNames.length > 0) {
    feedbackParts.push(
      invalidTypeNames.length === 1
        ? `"${invalidTypeNames[0]}" is not an allowed file type.`
        : `${invalidTypeNames.length} files are not an allowed type.`
    )
  }
  if (invalidSizeNames.length > 0) {
    feedbackParts.push(
      invalidSizeNames.length === 1
        ? `"${invalidSizeNames[0]}" exceeds the maximum size (${maxFileSizeDisplay.value}).`
        : `${invalidSizeNames.length} files exceed the maximum size (${maxFileSizeDisplay.value}).`
    )
  }
  if (feedbackParts.length > 0) {
    emit('validation-error', feedbackParts.join(' '))
  }

  if (accepted.length === 0) {
    return
  }

  const newFiles: UploadedFile[] = accepted.map((file, index) => {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
    const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg']
    const treatAsImagePreview =
      file.type.startsWith('image/') ||
      ((props.useUniversalIntakeValidation || props.usePimImageAndAssetValidation) &&
        imageExts.includes(ext) &&
        (!file.type || file.type === 'application/octet-stream'))
    return {
      id: `temp-${Date.now()}-${index}-${Math.random()}`,
      file: file,
      name: file.name,
      size: file.size,
      previewUrl: treatAsImagePreview ? URL.createObjectURL(file) : undefined,
      uploading: false,
    }
  })

  uploadedFiles.value = [...uploadedFiles.value, ...newFiles]

  emit('files-selected', accepted)
}

function requestRemove(index: number) {
  if (props.requireRemoveConfirmation) {
    pendingRemoveIndex.value = index
    showRemoveConfirm.value = true
  } else {
    removeFile(index)
  }
}

function onRemoveConfirm() {
  const idx = pendingRemoveIndex.value
  showRemoveConfirm.value = false
  pendingRemoveIndex.value = null
  if (idx !== null && idx >= 0) {
    removeFile(idx)
  }
}

function onRemoveCancel() {
  showRemoveConfirm.value = false
  pendingRemoveIndex.value = null
}

const removeFile = (index: number) => {
  const file = uploadedFiles.value[index]
  if (!file) return

  // Cleanup preview URL if it's a blob URL
  if (file.previewUrl && file.previewUrl.startsWith('blob:')) {
    URL.revokeObjectURL(file.previewUrl)
  }

  // If it's a temporary file (has file but no URL), remove it directly
  if (file.file && !file.url) {
    uploadedFiles.value.splice(index, 1)
    // Emit the removed file so parent can update its list
    emit('file-removed', index)
  } else {
    // If it's an uploaded file (has URL), emit event to parent for backend deletion
    emit('file-removed', index)
    // Don't remove from uploadedFiles immediately - let the watch handle it when modelValue updates
  }
}

const retryUpload = (file: UploadedFile, index: number) => {
  if (file.file) {
    file.uploading = true
    file.error = undefined
    file.progress = 0
    emit('files-selected', [file.file])
  }
}

const updateModelValue = () => {
  const urls = uploadedFiles.value
    .filter(f => f.url) // Only include uploaded files with URLs
    .map(f => f.url!)
  emit('update:modelValue', urls)
}

const getFileType = (filename: string): string => {
  const ext = filename.split('.').pop()?.toLowerCase()
  const types: Record<string, string> = {
    'jpg': 'JPG',
    'jpeg': 'JPEG',
    'png': 'PNG',
    'gif': 'GIF',
    'webp': 'WEBP',
    'pdf': 'PDF',
    'doc': 'DOC',
    'docx': 'DOCX',
    'xls': 'XLS',
    'xlsx': 'XLSX',
    'ppt': 'PPT',
    'pptx': 'PPTX',
    'txt': 'TXT',
    'csv': 'CSV',
    'indd': 'INDD',
    'indt': 'INDT'
  }
  return types[ext || ''] || ext?.toUpperCase() || 'FILE'
}

// No need to track uploaded files or mark them - we don't show temporary previews
// Files only appear after upload completes and modelValue is updated

// Expose method to update upload progress
const updateFileProgress = (fileId: string, progress: number) => {
  const file = uploadedFiles.value.find(f => f.id === fileId)
  if (file) {
    file.progress = progress
    file.uploading = true
  }
}

// Expose method to mark file as error
const markFileAsError = (fileId: string, error: string) => {
  const file = uploadedFiles.value.find(f => f.id === fileId)
  if (file) {
    file.error = error
    file.uploading = false
  }
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
}

const formatTotalSize = (): string => {
  const totalBytes = uploadedFiles.value.reduce((sum, file) => {
    // Use file.size if available (for File objects), otherwise 0
    return sum + (file.size || 0)
  }, 0)
  if (totalBytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(totalBytes) / Math.log(k))
  return Math.round(totalBytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
}

function onClearClick() {
  if (props.requireClearConfirmation) {
    showClearConfirm.value = true
  } else {
    clearAllFilesLegacy()
  }
}

function clearAllFilesLegacy() {
  emit('clear-all')
}

function onClearConfirm() {
  showClearConfirm.value = false
  clearAll()
  emit('clear-all')
}

function onClearCancel() {
  showClearConfirm.value = false
}

const isImageFile = (file: UploadedFile): boolean => {
  if (file.file) {
    return file.file.type.startsWith('image/')
  }
  if (file.url) {
    return /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(file.url)
  }
  return false
}

const handleImageError = (event: Event, file: UploadedFile) => {
  // Mark the file as having an image error so placeholder is shown
  file.imageError = true
}

// Cleanup object URLs on unmount
onUnmounted(() => {
  uploadedFiles.value.forEach(file => {
    if (file.previewUrl && file.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(file.previewUrl)
    }
  })
})

// Method to clear all files (including temporary ones)
const clearAll = () => {
  // Cleanup all preview URLs
  uploadedFiles.value.forEach(file => {
    if (file.previewUrl && file.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(file.previewUrl)
    }
  })
  uploadedFiles.value = []
  emit('update:modelValue', [])
}

/** Return staged files (selected but not yet uploaded) for save-driven upload. */
function getStagedFiles(): File[] {
  return uploadedFiles.value
    .filter(f => f.file && !f.url)
    .map(f => f.file!)
}

/** Remove only staged (temp) files; keep URL-based entries. Use e.g. on Cancel. */
function clearStagedFiles() {
  const toRemove = uploadedFiles.value.filter(f => f.file && !f.url)
  toRemove.forEach(f => {
    if (f.previewUrl && f.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(f.previewUrl)
    }
  })
  uploadedFiles.value = uploadedFiles.value.filter(f => !f.file || !!f.url)
}

// Expose methods for parent component (if needed for progress tracking)
defineExpose({
  updateFileProgress,
  markFileAsError,
  uploadedFiles: computed(() => uploadedFiles.value),
  clearAll,
  getStagedFiles,
  clearStagedFiles
})
</script>

<style scoped>
.multi-file-upload-wrapper {
  width: 100%;
}
</style>

